/**
 * Presentatie-onderdelen van de rustige projectpagina. Geen data-ophalen: alles via props,
 * zodat ze los te testen en te screenshotten zijn.
 */
import { useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { T, stitchTooltipSlotProps } from '../../ProjectOverview/parts/stitch';
import { R, labelSx, knopSx, tekstLinkSx } from './stijl';

/** Eén kaart "Volgende stap" met één primaire knop (en hooguit één tekstlink). */
export function VolgendeStapKaart({ stap, onActie }) {
  if (!stap) return null;
  const tekst = stap.daarna ? `${stap.tekst} Daarna: ${stap.daarna.titel}.` : stap.tekst;
  return (
    <Box component="section" aria-label="Volgende stap" data-testid="volgende-stap"
      sx={{ bgcolor: R.vlak, borderRadius: '16px', px: { xs: 2.5, md: 4 }, py: { xs: 2.75, md: 3.5 } }}>
      <Typography component="span" sx={labelSx}>Volgende stap</Typography>
      <Tooltip title={`Waarom: ${stap.waarom}`} slotProps={stitchTooltipSlotProps} placement="bottom-start" describeChild>
        <Typography component="h2" data-testid="volgende-stap-titel" sx={{
          fontFamily: T.font, fontWeight: 800, fontSize: '1.3rem', color: R.tekst, mt: 1.25, mb: 0.75,
          cursor: 'help', width: 'fit-content', maxWidth: '100%',
        }}>
          {stap.titel}
        </Typography>
      </Tooltip>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.94rem', color: R.tekst2, maxWidth: '60ch', lineHeight: 1.55 }}>
        {tekst}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2.5, rowGap: 1.5, flexWrap: 'wrap', mt: 2.75 }}>
        <Box component="button" type="button" onClick={() => onActie(stap.actie)} sx={knopSx}>
          {stap.actie.label}
        </Box>
        {stap.secundair && (
          <Box component="button" type="button" onClick={() => onActie(stap.secundair)} sx={tekstLinkSx}>
            {stap.secundair.label}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** "Wat er is": label + "+ Toevoegen", een stille lijst en één naslagregel. */
export function WatErIsLijst({ rijen, naslag, onOpen, onToevoegen }) {
  const leeg = !rijen.length && !naslag.length;
  return (
    <Box component="section" aria-label="Wat er is" sx={{ mt: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
        <Typography component="span" sx={labelSx}>Wat er is</Typography>
        <Tooltip title="Document, klantbriefing, notitie, taak of meeting toevoegen. Je kunt een bestand ook overal op de pagina slepen."
          slotProps={stitchTooltipSlotProps}>
          <Box component="button" type="button" onClick={onToevoegen} data-testid="toevoegen"
            sx={{ ...tekstLinkSx, color: R.accent }}>
            + Toevoegen
          </Box>
        </Tooltip>
      </Box>
      <Box sx={{ bgcolor: R.vlak, borderRadius: '16px', py: 0.75 }}>
        {leeg && (
          <Typography sx={{ px: { xs: 2.5, md: 4 }, py: 1.75, fontFamily: T.fontBody, fontSize: '0.88rem', color: R.stil }}>
            Nog niets bij dit project. Voeg een document, notitie of taak toe.
          </Typography>
        )}
        {rijen.map((r, i) => (
          <Box key={r.key} component="button" type="button" onClick={() => onOpen(r.key)} data-testid={`rij-${r.key}`}
            sx={{
              ...rijSx, borderTop: i > 0 ? `1px solid ${R.scheiding}` : 0,
              '&:hover .pijl': { color: R.accent }, '&:hover .wat': { color: R.tekst },
            }}>
            <Box component="span" className="wat" sx={{ color: R.stil, fontSize: '0.88rem', gridColumn: { xs: '1 / -1', md: 'auto' } }}>{r.label}</Box>
            <Box component="span" sx={{ fontWeight: 500, color: R.tekst, minWidth: 0 }}>
              {r.waarde}
              {r.context && (
                <Box component="small" sx={{ ml: 1, fontWeight: 400, fontSize: '0.88rem', color: r.probleem ? R.fout : R.stil }}>
                  {r.context}
                </Box>
              )}
            </Box>
            <Box component="span" className="pijl" aria-hidden sx={{ color: R.stiller, fontSize: '0.88rem' }}>›</Box>
          </Box>
        ))}
        {naslag.length > 0 && (
          <Box data-testid="naslag" sx={{ ...rijSx, cursor: 'default', borderTop: rijen.length ? `1px solid ${R.scheiding}` : 0 }}>
            <Box component="span" sx={{ color: R.stil, fontSize: '0.88rem', gridColumn: { xs: '1 / -1', md: 'auto' } }}>Naslag</Box>
            <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.25, rowGap: 0.5 }}>
              {naslag.map((n) => (
                <Box key={n.key} component="button" type="button" onClick={() => onOpen(n.key)}
                  sx={{ ...tekstLinkSx, fontSize: '0.94rem' }}>
                  <Box component="span" sx={{ color: R.tekst, fontWeight: 500 }}>{n.aantal}</Box> {n.label}
                </Box>
              ))}
            </Box>
            <span />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const rijSx = {
  display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', md: '150px minmax(0, 1fr) auto' },
  columnGap: 2, rowGap: 0.25, alignItems: 'baseline', width: '100%', textAlign: 'left',
  border: 0, bgcolor: 'transparent', cursor: 'pointer', fontFamily: T.fontBody, fontSize: '0.94rem',
  px: { xs: 2.5, md: 4 }, py: 1.85,
  '&:focus-visible': { outline: `2px solid ${R.accent}`, outlineOffset: -2, borderRadius: '8px' },
};

/** Ingeklapt blok "Projectacties": één regel tot je hem openklapt. */
export function ProjectactiesBlok({ groepen, open, onWissel }) {
  return (
    <Box sx={{ mt: 3.5 }}>
      <Box component="button" type="button" onClick={onWissel} aria-expanded={open} data-testid="projectacties"
        sx={{ ...tekstLinkSx, color: R.stil, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <Box component="span" aria-hidden sx={{ color: R.stiller, fontSize: '1.1rem', lineHeight: 1,
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }}>›</Box>
        Projectacties
      </Box>
      {open && (
        <Box sx={{ mt: 2, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groepen.filter((g) => g.items.length).map((g) => (
            <Box key={g.titel}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: R.stil, mb: 0.75 }}>{g.titel}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1 }}>
                {g.items.map((it) => (
                  <Tooltip key={it.key} title={it.uitleg || ''} slotProps={stitchTooltipSlotProps}>
                    <Box component="button" type="button" onClick={it.actie} sx={tekstLinkSx}>{it.label}</Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/** Rechterkolom: alleen blokken met inhoud. */
export function ZijKolom({ blokken }) {
  const echte = blokken.filter(Boolean);
  if (!echte.length) return null;
  return (
    <Box component="aside" sx={{ display: 'flex', flexDirection: 'column', gap: 4.5, pt: { md: 1 } }}>
      {echte.map((b) => (
        <Box key={b.titel}>
          <Typography component="span" sx={{ ...labelSx, display: 'block', mb: 1.25 }}>{b.titel}</Typography>
          {b.regels.filter(Boolean).map((r, i) => (
            <Typography key={i} sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', color: i === 0 ? R.tekst2 : R.stil, mt: i ? 0.5 : 0 }}>
              {r}
            </Typography>
          ))}
          {b.link && (
            <Box component="button" type="button" onClick={b.link.actie} sx={{ ...tekstLinkSx, display: 'inline-block', mt: 1 }}>
              {b.link.label}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

/** Kleine hook: open/dicht onthouden in localStorage (fouten = gewoon niet onthouden). */
export function useOnthouden(sleutel, standaard = false) {
  const [waarde, setWaarde] = useState(() => {
    try { const v = localStorage.getItem(sleutel); return v === null ? standaard : v === '1'; } catch { return standaard; }
  });
  const wissel = () => setWaarde((v) => {
    try { localStorage.setItem(sleutel, v ? '0' : '1'); } catch { /* privémodus */ }
    return !v;
  });
  return [waarde, wissel];
}
